import React from "react";
import PropTypes from "prop-types";
import { AlertCircle, CheckCircle2, CircleDashed, Plus } from "lucide-react";

import Button from "./Button";

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
  }).format(date);
};

export const formatPossessionRange = (start, end) => {
  const formattedStart = formatDateTime(start);
  const formattedEnd = formatDateTime(end);
  if (!formattedStart || !formattedEnd) return "Dates not complete";
  return `${formattedStart} to ${formattedEnd}`;
};

const PossessionNavigator = ({ activeIndex = 0, errors = [], items, onAdd, onSelect }) => (
  <nav aria-label="Possessions" className="wsm-possession-navigator">
    <div className="wsm-possession-navigator__scroller">
      <ol>
        {items.map((item, index) => {
          const hasErrors = item.errorCount > 0;
          const Icon = hasErrors ? AlertCircle : item.complete ? CheckCircle2 : CircleDashed;
          const status = hasErrors
            ? `${item.errorCount} ${item.errorCount === 1 ? "error" : "errors"}`
            : item.complete ? "Ready" : "Incomplete";

          return (
            <li key={item.id || index}>
              <button
                aria-current={activeIndex === index ? "step" : undefined}
                className="wsm-possession-navigator__item"
                onClick={() => onSelect(index)}
                type="button"
              >
                <span className="wsm-possession-navigator__index">{index + 1}</span>
                <span className="wsm-possession-navigator__details">
                  <strong>Part {index + 1}</strong>
                  <span>{formatPossessionRange(item.start, item.end)}</span>
                </span>
                <span className={`wsm-possession-navigator__status wsm-possession-navigator__status--${hasErrors ? "error" : item.complete ? "complete" : "incomplete"}`}>
                  <Icon aria-hidden="true" />
                  {status}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
    {onAdd && <Button icon={Plus} onClick={onAdd} size="compact" variant="outline">Add part</Button>}
    {errors.length > 0 && (
      <div className="wsm-possession-navigator__error" role="alert">
        <AlertCircle aria-hidden="true" />
        <div>{errors.map((message, index) => <p key={`${message}-${index}`}>{message}</p>)}</div>
      </div>
    )}
  </nav>
);

PossessionNavigator.propTypes = {
  activeIndex: PropTypes.number,
  errors: PropTypes.arrayOf(PropTypes.string),
  items: PropTypes.arrayOf(PropTypes.shape({
    complete: PropTypes.bool,
    end: PropTypes.string,
    errorCount: PropTypes.number,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    start: PropTypes.string,
  })).isRequired,
  onAdd: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
};

export default PossessionNavigator;
